/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'

import type { AnyCluster } from '@modules/ccm-lib/types'
import { getLinkedServiceUrl } from '@modules/ccm-lib/urls'
import { FormattedMessage } from '@modules/i18n'
import { useDisableCCMClusterService } from '@modules/ccm-user-lib/hooks'

// eslint-disable-next-line import/no-restricted-paths
import ExternalLink from '@/components/ExternalLink'

import { generateConnectAutoOpsWizardUrl } from '../../autoops-lib/util'

import ServiceItem from './ServiceItem'
import OptionalDocumentationLink from './OptionalDocumentationLink'

import type { FC } from 'react'

const AutoOpsServiceItem: FC<{
  cluster: AnyCluster
}> = ({ cluster }) => {
  const autoOpsUrl = getLinkedServiceUrl('auto_ops', cluster)

  const serviceData = cluster.services.auto_ops

  const { mutate: switchOffAutoOps } = useDisableCCMClusterService()

  const handleDisableAutoOps = () => {
    switchOffAutoOps({
      id: cluster.id,
      serviceName: 'auto_ops',
    })
  }

  const autoOpsConnectLink = generateConnectAutoOpsWizardUrl({
    organizationId: cluster.metadata.organization_id,
  })

  const isEnabled = serviceData.enabled

  return (
    <ServiceItem
      serviceName='AutoOps'
      badge={isEnabled ? 'enabled' : 'not_enabled'}
      zoneId={serviceData.config?.region_id}
      description={
        <FormattedMessage
          id='ccm.cluster-services.autoops.description'
          defaultMessage='Get instant cluster diagnostics, performance tips, and cost-saving recommendations—no extra management needed. <learnMoreLink></learnMoreLink>'
          values={{
            learnMoreLink: () => (
              <OptionalDocumentationLink cluster={cluster} serviceName='auto_ops' />
            ),
          }}
        />
      }
      showContextMenu={isEnabled}
      rightSideContent={
        isEnabled ? (
          <ExternalLink href={autoOpsUrl}>
            <FormattedMessage id='ccm.cluster-services.open-link' defaultMessage='Open' />
          </ExternalLink>
        ) : (
          <ExternalLink href={autoOpsConnectLink}>
            <FormattedMessage id='ccm.cluster-services.connect-link' defaultMessage='Connect' />
          </ExternalLink>
        )
      }
      onDisable={handleDisableAutoOps}
    />
  )
}

export default AutoOpsServiceItem
